import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as Popover from '@radix-ui/react-popover';
import {
  InformationCircleIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  DocumentCheckIcon,
} from '@heroicons/react/20/solid';

import { Button, Badge } from 'src/ui/components';

import { ActivityLogListType } from '../types/activityLogType';
import { classNames } from 'src/utils/className';
import { useAuth } from 'src/hooks';

const FilesPopover = ({
  activityLog,
  from,
}: {
  activityLog: ActivityLogListType;
  from?: string;
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [copiedFiles, setCopiedFiles] = useState(false);

  useEffect(() => {
    if (!open) return;

    const observer = new IntersectionObserver(entries => {
      if (entries[0] && !entries[0].isIntersecting) setOpen(false);
    });

    if (triggerRef.current) observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [open]);

  const files = from
    ? Array.from(new Set(activityLog?.DocId?.split(',')))
    : Array.from(new Set(activityLog?.ReportFileName?.split(',')));

  const handleCopyFileNames = () => {
    if (!files.length) return;
    navigator.clipboard.writeText(files.join('\n'));
    setCopiedFiles(true);
    setTimeout(() => {
      setCopiedFiles(false);
    }, 4000);
  };

  const getFormattedDateRange = (csvExportCriteria: string) => {
    if (!csvExportCriteria) return '';
    const { CsvExportStartDate, CsvExportEndDate } =
      JSON.parse(csvExportCriteria);
    return `${dayjs(CsvExportStartDate).format('MM/DD/YYYY H:mm:ss')} - ${dayjs(
      CsvExportEndDate,
    ).format('MM/DD/YYYY H:mm:ss')}`;
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          ref={triggerRef}
          variant="subtle"
          size="small"
          className="relative -ml-2"
        >
          <InformationCircleIcon
            className="-ml-0.5 h-5 w-5 text-primary"
            aria-hidden="true"
          />
          {activityLog?.Activity === 'CSV Download'
            ? 'CSV Download'
            : 'Multiple'}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          collisionPadding={20}
          className="z-10 min-w-72 max-w-[410px] border border-gray-400 bg-white text-sm shadow-xl dark:border-transparent dark:bg-neutral-700"
        >
          <div>
            <div className="flex items-center justify-between gap-2 border-b border-gray-300 px-3 py-2 dark:border-neutral-600">
              <h4 className="flex grow items-center gap-2 text-lg font-semibold">
                {activityLog?.Activity === 'CSV Download' ? 'CSV' : 'File(s)'}
                {activityLog?.Activity !== 'CSV Download' && (
                  <Badge variant="info">{files.length}</Badge>
                )}
              </h4>
              <Popover.Close asChild>
                <Button variant="subtle" size="small">
                  <XMarkIcon className="size-4" />
                </Button>
              </Popover.Close>
            </div>
            <div
              className={classNames(
                'max-h-72 overflow-y-auto',
                activityLog?.Activity !== 'CSV Download'
                  ? 'divide-y divide-gray-100 dark:divide-neutral-600/50'
                  : 'px-3 py-2',
              )}
            >
              {activityLog?.Activity === 'CSV Download' ? (
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-neutral-400">
                      Date Range
                    </dt>
                    <dd className="font-semibold">
                      {getFormattedDateRange(activityLog?.CSVExportCriteria)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-neutral-400">
                      Number of entries
                    </dt>
                    <dd className="font-semibold">
                      {activityLog?.TotalCallDocuments}
                    </dd>
                  </div>
                </dl>
              ) : (
                files.map((file, index) => (
                  <p
                    key={index}
                    className="whitespace-nowrap px-3 py-1.5 text-sm font-semibold leading-relaxed"
                  >
                    {file}
                  </p>
                ))
              )}
            </div>
            {activityLog?.Activity !== 'CSV Download' && (
              <div className="flex justify-center p-2">
                <Button
                  variant="link"
                  size="small"
                  onClick={handleCopyFileNames}
                  title={copiedFiles ? 'File names copied' : ''}
                  className={classNames(
                    copiedFiles
                      ? 'text-emerald-600 hover:text-emerald-600 hover:no-underline active:translate-y-0 dark:text-emerald-400 dark:hover:text-emerald-400'
                      : '',
                  )}
                >
                  {copiedFiles ? (
                    <DocumentCheckIcon className="size-4" />
                  ) : (
                    <DocumentDuplicateIcon className="size-4" />
                  )}

                  {copiedFiles ? 'Copied' : 'Copy all file names'}
                </Button>
              </div>
            )}
            <Popover.Arrow className="dark:fill-neutral-700" />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export { FilesPopover };
